import React, { useMemo, useRef } from 'react';
import classes from '../AnswersBlock.module.scss';
import classnames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import _ from 'lodash';

const INITIAL_DELAY = 0;

const getDelays = (num, speed) =>
  _.range(INITIAL_DELAY, speed * num + INITIAL_DELAY, speed);

const KeepGoingAnswers = ({
  useImages,
  animation,
  question,
  isResolved,
  isMultiplayer,
  animationSpeed,
  coloredAnswerBoxes,
  contentPath,
  colors,
  textStyle,
  clickHandler,
  visibleAfterResolvedTime,
  forceCorrectAnswerVisible = false,
  mobileInteraction = false,
  mobileSelectedAnswerId = null,
  workAreaStyle,
}) => {
  const answersDelays = useMemo(
    () => getDelays(question.answers.length, animationSpeed),
    [question, animationSpeed]
  );
  const selectionRects = useRef({});

  const isHorizontalAnimation =
    animation === 'leftToRight' || animation === 'rightToLeft';

  const keepMobileSelection = mobileInteraction && !!mobileSelectedAnswerId;
  const selectedAnswerIndex = keepMobileSelection
    ? question.answers.findIndex(answer => answer.id === mobileSelectedAnswerId)
    : -1;
  const selectedAnswer =
    selectedAnswerIndex >= 0 ? question.answers[selectedAnswerIndex] : null;
  const selectedRect = selectedAnswer
    ? selectionRects.current[selectedAnswer.id]
    : null;

  // Keep the animated answer set untouched while feedback is open. The real
  // answers are frozen/hidden in place; a static snapshot of the tapped answer
  // is rendered above them. Releasing the selection simply reveals the same
  // animated DOM again, so a wrong answer cannot restart or lose the answers.
  const mobileSelectionOverlay = Boolean(
    keepMobileSelection && selectedAnswer && selectedRect
  );

  return (
    <div
      className={classnames('moving-answers-work-area', {
        'moving-answers-work-area--four-answers': question.answers.length >= 4,
        [classes.horizontalKeepGoingBlock]: isHorizontalAnimation,
        [classes.verticalKeepGoingBlock]: !isHorizontalAnimation,
        [classes.multiplayer]: isMultiplayer,
      })}
      style={workAreaStyle}
    >
      {question.answers.map((answer, index) => {
        const animationDuration = animationSpeed;
        const animationDelay = answersDelays[index];
        const revealCorrectAnswer = forceCorrectAnswerVisible && answer.correct;

        const answerPostition = useImages
          ? {
              left: isHorizontalAnimation
                ? 'auto'
                : (100 / question.answers.length) * index +
                  100 / (question.answers.length * 2) +
                  '%',
              top: isHorizontalAnimation
                ? (100 / (question.answers.length * 2)) * index +
                  100 / (question.answers.length * 2) +
                  '%'
                : 'auto',
              transform: 'translateX(-1000%)'
            }
          : {
              left: isHorizontalAnimation
                ? 'auto'
                : (100 / question.answers.length) * index +
                  100 / (question.answers.length * 2) +
                  '%',
              top: isHorizontalAnimation
                ? (100 / question.answers.length) * index +
                  100 / (question.answers.length * 2) +
                  '%'
                : 'auto',
              transform: 'translateX(-1000%)'
            };

        return (
          <CSSTransition
            appear
            unmountOnExit
            mountOnEnter
            in={!isResolved || revealCorrectAnswer || keepMobileSelection}
            key={answer.id}
            timeout={{
              // On native mobile, allow the CSS appear transition to finish
              // before switching to the continuous movement class. Otherwise
              // the first answer (animationDelay=0) jumps straight to entered
              // and never shows its entrance effect.
              appear: animationDelay + (mobileInteraction ? animationDuration : 0),
              exit:
                animationDuration +
                (answer.correct && !question.fillIn
                  ? visibleAfterResolvedTime * 1000
                  : 0)
            }}
            classNames={{
              appearActive: classes.fadeIn,
              enterDone: classes[animation],
              exit: classes[animation],
              exitActive: classes[animation]
            }}
          >
            {state => {
              const animationProps = {
                animationDuration:
                  (state === 'entering'
                    ? animationDuration / 1000
                    : (animationDuration * 10) / 1000) + 's',
                animationDelay:
                  (state === 'entering'
                    ? animationDelay / 4 / 1000
                    : animationDelay / 1000) + 's',
                animationPlayState:
                  isResolved || keepMobileSelection ? 'paused' : 'running'
              };

              return (
                <div
                  className={classnames(classes.keepGoingWrapper, {
                    [classes.imageAnswerWrapper]: useImages,
                    [classes.textAnswerWrapper]: !useImages,
                  })}
                  style={revealCorrectAnswer
                    ? {
                        left: '50%',
                        right: 'auto',
                        top: '50%',
                        bottom: 'auto',
                        transform: 'translate(-50%, -50%)',
                        animation: 'none',
                        animationPlayState: 'paused',
                        zIndex: 50,
                      }
                    : {
                        ...answerPostition,
                        ...animationProps,
                        visibility: mobileSelectionOverlay ? 'hidden' : undefined,
                        pointerEvents: mobileSelectionOverlay ? 'none' : undefined,
                      }}
                >
                  <button
                    className={classnames(classes.answerBtn, 'moving-answers-answer-button', {
                      [classes.fadeOut]: state === 'exiting' && !revealCorrectAnswer
                    })}
                    disabled={isResolved || state === 'entering' || keepMobileSelection}
                    style={{
                      ...textStyle,
                      ...(coloredAnswerBoxes ? colors[index] : {}),
                      animationDuration: animationDuration / 1000 + 's',
                      animationDelay:
                        (answer.correct && !question.fillIn
                          ? visibleAfterResolvedTime
                          : 0) + 's',
                      ...(revealCorrectAnswer
                        ? { transform: 'none', animation: 'none', opacity: 1 }
                        : {}),
                    }}
                    onTouchEnd={mobileInteraction
                      ? (event) => {
                          event.preventDefault();
                          event.stopPropagation();

                          const workArea = event.currentTarget.closest(
                            '.moving-answers-work-area'
                          );
                          const answerRect = event.currentTarget.getBoundingClientRect();
                          const workAreaRect = workArea && workArea.getBoundingClientRect();

                          if (answerRect && workAreaRect) {
                            selectionRects.current[answer.id] = {
                              left: answerRect.left - workAreaRect.left,
                              top: answerRect.top - workAreaRect.top,
                              width: answerRect.width,
                              height: answerRect.height,
                            };
                          }

                          clickHandler(answer);
                        }
                      : undefined}
                    onClick={mobileInteraction ? undefined : clickHandler.bind(null, answer)}
                  >
                    {useImages ? (
                      <img src={contentPath + answer.image} alt="" />
                    ) : (
                      answer.text
                    )}
                  </button>
                </div>
              );
            }}
          </CSSTransition>
        );
      })}

      {mobileSelectionOverlay && (
        <div
          data-mobile-selection-overlay="true"
          className={classnames(
            classes.keepGoingWrapper,
            useImages ? classes.imageAnswerWrapper : classes.textAnswerWrapper
          )}
          style={{
            left: selectedRect.left,
            right: 'auto',
            top: selectedRect.top,
            bottom: 'auto',
            width: selectedRect.width,
            height: selectedRect.height,
            transform: 'none',
            animation: 'none',
            animationPlayState: 'paused',
            zIndex: 60,
            pointerEvents: 'none',
          }}
        >
          <button
            className={classnames(classes.answerBtn, 'moving-answers-answer-button')}
            disabled
            style={{
              ...textStyle,
              ...(coloredAnswerBoxes ? colors[selectedAnswerIndex] : {}),
              width: '100%',
              height: '100%',
              transform: 'none',
              animation: 'none',
              opacity: 1,
            }}
          >
            {useImages ? (
              <img src={contentPath + selectedAnswer.image} alt="" />
            ) : (
              selectedAnswer.text
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default KeepGoingAnswers;
